import React, { useState, useEffect, useMemo } from 'react'

import { MsalProvider } from '@azure/msal-react'
import { onlineManager } from '@tanstack/react-query'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { ThemeProvider } from 'styled-components'

import { Session } from '@contexts/auth'
import { MockEnvironmentProvider } from '@contexts/mockEnv'
import { queryClient } from '@services/queryClient'
import { createIDBPersister } from '@services/idbPersister'
import { msalInstance } from '@services/msalConfig'
import { AppRoutes } from '@services/routes'

import { CustomDatadogProvider } from './Datadog'
import { light, dark } from './src/theme'

// Set online status synchronously BEFORE any component renders or query fires.
// This prevents queries from attempting network requests while offline.
onlineManager.setOnline(navigator.onLine)

const persister = createIDBPersister()

export default function App() {
  const [appIsReady, setAppIsReady] = useState(false)

  const isDarkMode = useMemo(() => {
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false
  }, [])

  const isDev = import.meta.env.VITE_ENV_NAME?.toLowerCase() === 'development'

  useEffect(() => {
    const handleOnline = () => onlineManager.setOnline(true)
    const handleOffline = () => onlineManager.setOnline(false)

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  useEffect(() => {
    setAppIsReady(true)
  }, [])

  if (!appIsReady) {
    return null
  }

  return (
    <CustomDatadogProvider>
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{ persister, maxAge: 1000 * 60 * 60 * 24 }}
      >
        <ThemeProvider theme={isDarkMode ? dark : light}>
          <MsalProvider instance={msalInstance}>
            <MockEnvironmentProvider>
              <Session>
                <AppRoutes />
              </Session>
            </MockEnvironmentProvider>
          </MsalProvider>
          {isDev && <ReactQueryDevtools initialIsOpen={false} />}
        </ThemeProvider>
      </PersistQueryClientProvider>
    </CustomDatadogProvider>
  )
}
